/**
 * View Operations
 *
 * TypeScript implementation of view toggle operations.
 * Replaces legacy functions: ismertJelolesek, jelolesek, setMaszk.
 */

import type { Puzzle } from '../core/Puzzle';
import type { AppState } from '../state/AppState';

/** Context for view operations */
export interface ViewOperationContext {
    puzzle: Puzzle;
    appState: AppState;
    onMenuUpdate: () => void;
}

/**
 * Toggle known cell marking (Ismert mezők)
 * Shows/hides the values in known cells
 * - When marking is ON: Show all valid character values
 * - When marking is OFF: Only show values in "known" (ismert) cells
 */
export function toggleKnownMarking(
    element: HTMLElement,
    ctx: ViewOperationContext,
): void {
    const regex = new RegExp(ctx.appState.validCharPattern, 'g');

    // Update all cells based on the CURRENT state (before toggle)
    // This matches legacy behavior where:
    // - When current state is OFF (turning ON): show only known cells
    // - When current state is ON (turning OFF): show known + valid char cells
    for (let i = 0; i < ctx.puzzle.length; i++) {
        const cell = ctx.puzzle.getCell(i);
        if (!cell) continue;

        const cellEl = document.getElementById(`mezo-${i}`);
        if (!cellEl) continue;

        // Skip black, image, empty cells
        if (cell.isBlack || cell.isImage || cell.isEmpty) continue;

        cellEl.innerHTML = '';

        // Reset regex lastIndex
        regex.lastIndex = 0;

        // Show value if:
        // - Cell is known (ismert), OR
        // - Current marking is ON AND cell has valid character
        if (
            cell.isKnown ||
            (ctx.appState.knownMarking && regex.test(cell.value || ''))
        ) {
            cellEl.innerHTML = cell.value || '';
        }
    }

    // Toggle AFTER updating cells (legacy behavior)
    ctx.appState.knownMarking = !ctx.appState.knownMarking;
    ctx.onMenuUpdate();
    element.focus();
}

/**
 * Toggle clue marking (Meghatározások helyei)
 * Shows/hides the clue location indicators
 */
export function toggleClueMarking(
    element: HTMLElement,
    ctx: ViewOperationContext,
): void {
    // Only allowed in builder mode
    if (!ctx.appState.isBuilderMode) return;

    const wasMarking = ctx.appState.clueMarking;

    // Update background images for all cells
    for (let i = 0; i < ctx.puzzle.length; i++) {
        const cell = ctx.puzzle.getCell(i);
        if (!cell) continue;

        const cellEl = document.getElementById(`mezo-${i}`);
        if (!cellEl) continue;

        if (wasMarking) {
            // Turning off marking - show only warning backgrounds
            cellEl.style.backgroundImage = '';
            if (cell.hasExpressionError) {
                cellEl.style.backgroundImage = getWarningBackground();
            }
        } else {
            // Turning on marking - show ALL clue backgrounds (no direction filter)
            // Legacy: getBackgroundImage(i) with no direction shows both v and f clues
            cellEl.style.backgroundImage = cell.getBackgroundImage();
        }
    }

    // When turning off, also update current selection's background images
    // Like legacy: updateBackgroundImage(getTeljesTomb(g_aktualisTomb), g_mezoIrany)
    if (wasMarking) {
        const currentSelection = ctx.appState.currentSelection;
        const currentCellId = ctx.appState.currentCellId;

        // Update current cell
        const currentCell = ctx.puzzle.getCell(currentCellId);
        const currentCellEl = document.getElementById(`mezo-${currentCellId}`);
        if (currentCell && currentCellEl) {
            currentCellEl.style.backgroundImage =
                currentCell.getBackgroundImage(
                    ctx.appState.isHorizontal,
                    true, // onlyWarning
                );
        }

        // Update selection cells
        for (const cellId of currentSelection) {
            const cell = ctx.puzzle.getCell(cellId);
            const cellEl = document.getElementById(`mezo-${cellId}`);
            if (cell && cellEl) {
                cellEl.style.backgroundImage = cell.getBackgroundImage(
                    ctx.appState.isHorizontal,
                    true, // onlyWarning
                );
            }
        }
    }

    ctx.appState.clueMarking = !ctx.appState.clueMarking;
    ctx.appState.maskMarking = false;
    ctx.onMenuUpdate();
    element.focus();
}

/**
 * Restore cell border/dimension when hiding mask.
 * Like legacy setMaszk when g_maszkJeloles is true.
 */
function restoreCellBorder(cellEl: HTMLElement): void {
    if (cellEl.clientWidth === 28) {
        cellEl.style.borderRight = '1px solid rgb(213, 213, 213)';
        cellEl.style.width = '21px';
    }
    if (cellEl.clientHeight === 28) {
        cellEl.style.borderBottom = '1px solid rgb(213, 213, 213)';
        cellEl.style.height = '19px';
    }
}

/**
 * Remove cell border to allow mask to span cells.
 * Like legacy setMaszk when g_maszkJeloles is false.
 */
function removeCellBorder(cellEl: HTMLElement): void {
    if (cellEl.clientWidth === 27) {
        cellEl.style.borderRight = '0px';
        cellEl.style.width = '22px';
    }
    if (cellEl.clientHeight === 27) {
        cellEl.style.borderBottom = '0px';
        cellEl.style.height = '20px';
    }
}

/**
 * Toggle mask marking (Maszk megjelenítése)
 * Shows/hides the mask overlay on image cells.
 * Like legacy setMaszk: also adjusts border/dimension for mask images.
 */
export function toggleMaskMarking(
    element: HTMLElement,
    ctx: ViewOperationContext,
): void {
    // Check if allowed
    const canToggle =
        (ctx.appState.allowImages && ctx.appState.hasGraphics) ||
        (!ctx.appState.isBuilderMode && ctx.appState.hasGraphics);

    if (!canToggle) return;

    const maskImages = ctx.puzzle.getMaskImages();

    for (const mask of maskImages) {
        const cell = ctx.puzzle.getCell(mask.cellId);
        if (!cell?.isImage) continue;

        const cellEl = document.getElementById(
            `mezo-${mask.cellId}`,
        ) as HTMLElement;
        if (!cellEl) continue;

        if (ctx.appState.maskMarking) {
            // Hide mask - show normal background
            cellEl.style.backgroundImage = cell.getBackgroundImage(
                ctx.appState.isHorizontal,
            );
            restoreCellBorder(cellEl);
        } else {
            // Show mask
            cellEl.style.backgroundImage = `url(data:image/jpeg;base64,${mask.base64Data})`;
            removeCellBorder(cellEl);
        }
    }

    ctx.appState.maskMarking = !ctx.appState.maskMarking;
    ctx.onMenuUpdate();
    element.focus();
}

/**
 * Get warning background CSS
 */
function getWarningBackground(): string {
    return 'url(/img/hatter_hiba.png)';
}
